import {
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Box,
  ExportMenuItems,
  Flex,
  Icon,
  IconName,
  OTCCouponPeriodStatusEnum,
  useBlotterTableExtrasMenu,
  usePortal,
  useWsBlotterTable,
  VStack,
  type ColumnOrColumnGroup,
  type CompositePipeFunction,
  type WebsocketRequest,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { map } from 'rxjs/operators';
import type { DealCashflowsPanelConfig } from '../../../../types/LayoutConfig';
import type { DealCouponSchedule } from '../../../../types/OTC';
import { Panel } from '../../../Layout/Components/Panel';
import type { LayoutPanelProps } from '../../types';
import { DealCashflowsHeader } from './DealCashflowsHeader';
import { getDealCouponScheduleAmount } from './getDealCouponScheduleAmount';

interface DealCouponScheduleRequest extends WebsocketRequest {
  name: 'DealCouponSchedule';
  DealID: string;
}

type DealCouponScheduleBlotterRow = DealCouponSchedule & {
  rowID: string;
  /** The amount of the asset transaction. */
  Amount?: string;
};

const CURRENT_PERIOD_INDICATOR_COLUMN_ID = 'currentPeriodIndicator';

function CurrentPeriodIndicator({ data }: { data: DealCouponSchedule | undefined }) {
  return data?.CashflowStatus === OTCCouponPeriodStatusEnum.Current ? (
    <Icon aria-label="Current period" color="colors.blue.lighten" icon={IconName.TriangleRight} size={20} />
  ) : null;
}

export function getDealCouponScheduleRowID({ DealID, InternalID }: Pick<DealCouponSchedule, 'DealID' | 'InternalID'>) {
  return `${DealID}/${InternalID}`;
}

export function DealCashflowsPanel({ api, params }: LayoutPanelProps<DealCashflowsPanelConfig>) {
  return (
    <Panel panelApi={api} display="flex" flexDirection="column" data-testid="deal-cashflows-panel">
      <DealCashflowsHeader key={params.dealID} dealID={params.dealID} panelID={api.id} />
      <Box flex="1 1 0" overflow="hidden">
        <DealCashflowsTable dealID={params.dealID} panelID={api.id} />
      </Box>
    </Panel>
  );
}

function DealCashflowsTable({ dealID, panelID }: { dealID: string; panelID: string }) {
  const dealCouponScheduleRequest = useMemo(
    () =>
      ({
        name: 'DealCouponSchedule',
        DealID: dealID,
        tag: `DealCashflowsPanel/${panelID}/${dealID}`,
      }) satisfies DealCouponScheduleRequest,
    [dealID, panelID]
  );

  const columns = useMemo<ColumnOrColumnGroup[]>(
    () => [
      {
        type: 'custom',
        id: CURRENT_PERIOD_INDICATOR_COLUMN_ID,
        width: 40,
        sortable: false,
        frozen: true,
        exportable: false,
        params: {
          cellRenderer: CurrentPeriodIndicator,
          headerName: '',
          headerClass: 'no-handle',
          suppressColumnsToolPanel: true,
          suppressFiltersToolPanel: true,
          suppressHeaderMenuButton: true,
        },
      },
      {
        type: 'number',
        field: 'InternalID',
        hide: true,
        sort: '+',
        sortable: false,
        exportable: false,
        suppressColumnsToolPanel: true,
      },
      {
        type: 'prettyText',
        field: 'RowType',
        title: 'Type',
        description: 'Type of asset transaction.',
        width: 120,
        sortable: false,
      },
      {
        type: 'date',
        field: 'PaymentDatetime',
        title: 'Payment Due Date',
        description: 'When this cashflow is paid or scheduled to be paid.',
        sortable: false,
      },
      {
        type: 'date',
        field: 'PeriodStartDatetime',
        title: 'Period Start',
        sortable: false,
      },
      {
        type: 'date',
        field: 'PeriodEndDatetime',
        title: 'Period End',
        sortable: false,
      },
      {
        type: 'size',
        field: 'Amount',
        title: 'Amount',
        description: 'Amount of the asset transaction.',
        params: { currencyField: 'Currency', omitZero: true },
        sortable: false,
      },
      {
        type: 'size',
        field: 'PV',
        title: 'PV',
        description: 'Present Value',
        params: { currencyField: 'Currency', omitZero: true },
        sortable: false,
      },
    ],
    []
  );
  const couponScheduleRowIDPipe = useMemo<CompositePipeFunction<DealCouponScheduleBlotterRow, DealCouponSchedule>>(
    () => source =>
      source.pipe(
        map(response => ({
          ...response,
          data: response.data.map(schedule => ({
            ...schedule,
            rowID: getDealCouponScheduleRowID(schedule),
            Amount: getDealCouponScheduleAmount(schedule),
          })),
        }))
      ),
    []
  );
  const extrasMenuPopover = useBlotterTableExtrasMenu();
  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  const blotterTable = useWsBlotterTable<DealCouponScheduleRequest, DealCouponScheduleBlotterRow, DealCouponSchedule>({
    request: dealCouponScheduleRequest,
    rowID: 'rowID',
    columns,
    pipe: couponScheduleRowIDPipe,
    exportOptions: { name: 'DealCashflows' },
  });

  return (
    <>
      <BlotterTableFilters
        {...blotterTable.blotterTableFiltersProps}
        quickFilterPlaceholder="Search..."
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
          </BlotterTableExtrasMenu>
        }
      />
      <VStack h="100%" background="backgroundContent" alignItems="flex-start">
        <Flex ref={filtersContainerRef} alignSelf="stretch" justifyContent="flex-end" pt="spacing8" />
        <Box flex={1} alignSelf="stretch">
          <BlotterTable {...blotterTable} />
        </Box>
      </VStack>
    </>
  );
}
